import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Map, Overlay, ZoomControl } from 'pigeon-maps';
import { SelectCurrentCoordinates } from '_redux/slices/timeline/timeline-live-location-tab';
import { selectAirportLocations } from '_redux/slices/timeline/timeline-areas';
import { fetchTrailEvents } from '_services/coverage-service';
import { resolveWifiEventsToCoords, matchBle, bleToCoords } from './asset-tracking-utils';
import { MdSatelliteAlt, MdMap } from 'react-icons/md';
import { FaRegCompass } from 'react-icons/fa';
import StateManagedSelect from 'react-select';

const BRAND = '#307FE2';

function tsToDate(at) {
    return at?.toDate ? at.toDate() : new Date((at?.seconds ?? 0) * 1000);
}
const OSM_PROVIDER = (x, y, z) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
const SATELLITE_PROVIDER = (x, y, z) =>
    `https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

// Catmull-Rom → SVG cubic bezier path
function catmullRomPath(pts) {
    if (pts.length < 2) return '';
    let d = `M ${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(0, i - 1)];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[Math.min(pts.length - 1, i + 2)];
        const c1x = p1[0] + (p2[0] - p0[0]) / 6;
        const c1y = p1[1] + (p2[1] - p0[1]) / 6;
        const c2x = p2[0] - (p3[0] - p1[0]) / 6;
        const c2y = p2[1] - (p3[1] - p1[1]) / 6;
        d += ` C ${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
    }
    return d;
}

// SVG trail — placed inside <Map>, pigeon-maps injects latLngToPixel + mapState ─
// No anchor prop → pigeon-maps still injects coords, SVG sits at (0,0) in children container
function TrailPolyline({ points, latLngToPixel, mapState }) {
    if (!latLngToPixel || !mapState?.width || !mapState?.height || points.length < 2) return null;
    const { width, height } = mapState;
    const pixels = points.map(p => latLngToPixel([p.lat, p.lng]));
    const d = catmullRomPath(pixels);
    return (
        <svg style={{
            position: 'absolute', left: 0, top: 0, width, height,
            overflow: 'visible', pointerEvents: 'none', zIndex: 1,
        }}>
            <defs>
                <linearGradient id="trailGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={BRAND} stopOpacity="0.4" />
                    <stop offset="100%" stopColor={BRAND} stopOpacity="0.9" />
                </linearGradient>
            </defs>
            <path d={d} fill="none" stroke="url(#trailGrad)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// Resolve events to trail points (wifiCoords = Map<eventIndex, {lat, lng}>)
function resolveTrailPoints(events, locations, wifiCoords) {
    return events.flatMap((evt, i) => {
        const lat = evt.decoded?.lat;
        const lng = evt.decoded?.lng;
        if (lat != null && lng != null) {
            return [{ lat, lng, type: 'gps', at: evt.at, location: null }];
        }
        const wc = wifiCoords?.get(i);
        if (wc) {
            return [{ lat: wc.lat, lng: wc.lng, type: 'wifi', at: evt.at, location: null, locationId: wc.locationId, freeTextLabel: wc.freeTextLabel, floorLabel: wc.floorLabel }];
        }
        const bleItems = evt.decoded?.ble ?? [];
        const coords = bleToCoords(bleItems, locations);
        if (!coords) return [];
        const title = matchBle(bleItems, locations);
        return [{ lat: coords.latitude, lng: coords.longitude, type: 'ble', at: evt.at, location: title }];
    });
}

function fitToPoints(points) {
    if (!points.length) return null;
    const lats = points.map(p => p.lat);
    const lngs = points.map(p => p.lng);
    const center = [(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2];
    const spread = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs));
    const zoom = spread < 0.0005 ? 19 : spread < 0.002 ? 18 : spread < 0.01 ? 16 : spread < 0.05 ? 14 : 12;
    return { center, zoom };
}

// Default window helpers
function todayStr() {
    return new Date().toISOString().slice(0, 10);
}
function twelveHoursAgoTimeStr() {
    const d = new Date(Date.now() - 12 * 60 * 60 * 1000);
    return d.toTimeString().slice(0, 5);
}

// Main component
export default function AssetTrackingTrail({ devices, initialDevice }) {
    const airportCoords = useSelector(SelectCurrentCoordinates);
    const airportLocations = useSelector(state => state.timelineAreas.airportLocations);
    const locationById = useMemo(() => Object.fromEntries((airportLocations ?? []).map(l => [l._id, l.title])), [airportLocations]);
    const defaultCenter = airportCoords?.lat
        ? [parseFloat(airportCoords.lat), parseFloat(airportCoords.lon)]
        : [1.3521, 103.8198];

    const [search, setSearch] = useState('');
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [startDate, setStartDate] = useState(todayStr);
    const [startTime, setStartTime] = useState(twelveHoursAgoTimeStr);
    const [endDate,   setEndDate]   = useState(todayStr);
    const [endTime,   setEndTime]   = useState(() => new Date().toTimeString().slice(0, 5));
    const [trailPoints, setTrailPoints] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeFixIdx, setActiveFixIdx] = useState(null);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [mapZoom, setMapZoom] = useState(17);
    const [satelliteView, setSatelliteView] = useState(false);

    const filteredDevices = useMemo(() =>
        (devices ?? []).filter(d =>
            !search || d.meta?.deviceName?.toLowerCase().includes(search.toLowerCase())
        ), [devices, search]);

    const stats = useMemo(() => {
        if (!trailPoints.length) return null;
        const ble  = trailPoints.filter(p => p.type === 'ble').length;
        const gps  = trailPoints.filter(p => p.type === 'gps').length;
        const wifi = trailPoints.filter(p => p.type === 'wifi').length;
        const oldest = trailPoints[0]?.at;
        const newest = trailPoints[trailPoints.length - 1]?.at;
        return { total: trailPoints.length, ble, gps, wifi, oldest, newest };
    }, [trailPoints]);

    const loadTrail = useCallback(async (device, startOverride, endOverride) => {
        if (!device) return;
        setLoading(true);
        setError(null);
        setTrailPoints([]);
        setActiveFixIdx(null);
        try {
            const tenantId = device.meta?.tenantId;
            const appName = device.meta?.applicationName;
            const devEui = device.id;
            const start = startOverride ?? new Date(`${startDate}T${startTime}`);
            const end   = endOverride   ?? new Date(`${endDate}T${endTime}`);
            const events = await fetchTrailEvents(tenantId, appName, devEui, start, end);
            const wifiCoords = await resolveWifiEventsToCoords(events, device.meta?.tenantName);
            const points = resolveTrailPoints(events, airportLocations ?? [], wifiCoords);
            setTrailPoints(points);
            if (points.length > 0) {
                const fit = fitToPoints(points);
                if (fit) { setMapCenter(fit.center); setMapZoom(fit.zoom); }
            } else {
                setError('No position fixes found in this time window.');
            }
        } catch (e) {
            console.error('Trail load error:', e);
            setError('Failed to load trail. ' + (e?.message ?? ''));
        } finally {
            setLoading(false);
        }
    }, [startDate, startTime, endDate, endTime, airportLocations]);

    // Auto-load when opened via "See Trail"
    useEffect(() => {
        if (!initialDevice) return;
        const now   = new Date();
        const start = new Date(now.getTime() - 12 * 60 * 60 * 1000);
        setSelectedDevice(initialDevice);
        setStartDate(todayStr());
        setStartTime(twelveHoursAgoTimeStr());
        setEndDate(todayStr());
        setEndTime(now.toTimeString().slice(0, 5));
        loadTrail(initialDevice, start, now);
    }, [initialDevice]);

    const provider = useMemo(
        () => (satelliteView ? SATELLITE_PROVIDER : OSM_PROVIDER),
        [satelliteView]
    );

    const resetMap = () => {
        const fit = trailPoints.length ? fitToPoints(trailPoints) : null;
        if (fit) { setMapCenter(fit.center); setMapZoom(fit.zoom); }
        else { setMapCenter(defaultCenter); setMapZoom(17); }
    };

    return (
        <div style={{ display: 'flex', height: '100%', gap: 12 }}>

            {/* ── Left panel ── */}
            <div style={{
                width: 250, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 0,
                background: 'linear-gradient(175deg, #1e5fbb 0%, #307FE2 100%)',
                borderRadius: 12, overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(48,127,226,0.3)',
            }}>
                {/* Header */}
                <div style={{ padding: '14px 14px 12px', borderBottom: '1px solid rgba(255,255,255,0.12)', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>Trail</div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 14 }}>

                    {/* Tracker selector */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                                Select Tracker
                            </div>
                            <span style={{ fontSize: '0.65rem', fontWeight: 800, background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 20, padding: '1px 7px' }}>
                                {filteredDevices.length}
                            </span>
                        </div>

                        <input
                            type="text"
                            placeholder="Search devices…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{
                                width: '100%', padding: '6px 10px', boxSizing: 'border-box',
                                background: 'rgba(255, 255, 255, 0.62)', border: '1px solid rgba(255,255,255,0.22)',
                                borderRadius: 7, fontSize: '0.8rem', outline: 'none', color: '#090909',
                            }}
                        />

                        <div style={{ borderRadius: 8, overflowY: 'scroll', height: 190, background: 'rgba(0,0,0,0.12)', padding: 4 }}>
                            {filteredDevices.map(device => {
                                const active = selectedDevice?.id === device.id;
                                return (
                                    <div
                                        key={device.id}
                                        onClick={() => { setSelectedDevice(device); setTrailPoints([]); setError(null); setActiveFixIdx(null); }}
                                        style={{
                                            display: 'flex', alignItems: 'stretch',
                                            width: '100%', boxSizing: 'border-box',
                                            background: active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)',
                                            border: '1px solid rgba(255,255,255,0.12)',
                                            borderRadius: 8, marginBottom: 4, cursor: 'pointer',
                                            overflow: 'hidden', transition: 'background 0.12s', userSelect: 'none',
                                        }}
                                        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
                                        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                                    >
                                        <div style={{ width: 3, background: active ? '#fff' : 'transparent', flexShrink: 0 }} />
                                        <div style={{ padding: '7px 9px', minWidth: 0 }}>
                                            <div style={{ fontSize: '0.8rem', fontWeight: active ? 700 : 500, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {device.meta?.deviceName || device.id}
                                            </div>
                                            {device.meta?.deviceName && (
                                                <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {device.id}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {filteredDevices.length === 0 && (
                                <div style={{ padding: '16px 10px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                                    No devices found
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }} />

                    {/* Time window */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                            Time Window
                        </div>

                        {/* Start */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)' }}>Start</label>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                                    style={{ width: 110, boxSizing: 'border-box', fontSize: '0.78rem', padding: '5px 6px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.12)', color: '#fff', colorScheme: 'dark' }}
                                />
                                <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                                    style={{ width: 100, boxSizing: 'border-box', fontSize: '0.78rem', padding: '5px 6px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.12)', color: '#fff', colorScheme: 'dark' }}
                                />
                            </div>
                        </div>

                        {/* End */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)' }}>End</label>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                                    style={{ width: 110, boxSizing: 'border-box', fontSize: '0.78rem', padding: '5px 6px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.12)', color: '#fff', colorScheme: 'dark' }}
                                />
                                <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                                    style={{ width: 100, boxSizing: 'border-box', fontSize: '0.78rem', padding: '5px 6px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.12)', color: '#fff', colorScheme: 'dark' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Load button */}
                    <button
                        onClick={() => loadTrail(selectedDevice)}
                        disabled={!selectedDevice || loading}
                        style={{
                            padding: '8px 0', borderRadius: 7, border: 'none', fontWeight: 700, fontSize: '0.83rem',
                            background: (!selectedDevice || loading) ? 'rgba(255,255,255,0.12)' : '#fff',
                            color: (!selectedDevice || loading) ? 'rgba(255,255,255,0.35)' : BRAND,
                            cursor: (!selectedDevice || loading) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.15s',
                        }}
                    >
                        {loading ? 'Loading…' : 'Load Trail'}
                    </button>

                    {/* Stats */}
                    {stats && (
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                                Trail Summary
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)' }}>
                                <span>Total fixes</span><span style={{ fontWeight: 600 }}>{stats.total}</span>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {[['BLE', stats.ble], ['GPS', stats.gps], ['WiFi', stats.wifi]].map(([label, count]) => count > 0 && (
                                    <span key={label} style={{ flex: 1, textAlign: 'center', padding: '3px 0', borderRadius: 6, background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        {label} {count}
                                    </span>
                                ))}
                            </div>
                            {stats.oldest && (
                                <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
                                    {tsToDate(stats.oldest).toLocaleString()} → {tsToDate(stats.newest).toLocaleString()}
                                </div>
                            )}
                        </div>
                    )}

                    {error && (
                        <div style={{ fontSize: '0.75rem', color: '#fef3c7', background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: '7px 10px', lineHeight: 1.4, border: '1px solid rgba(255,211,77,0.3)' }}>
                            {error}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Map panel ── */}
            <div style={{ flex: 1, position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1px solid #dbeafe' }}>

                {/* Empty state */}
                {!selectedDevice && (
                    <div style={{
                        position: 'absolute', inset: 0, zIndex: 10,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(240,246,255,0.92)', borderRadius: 10, pointerEvents: 'none', gap: 10,
                    }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%', background: '#E6F0FB',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke={BRAND} strokeWidth="1.8">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e3a5f' }}>Select a tracker</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Choose a device and load its trail</div>
                    </div>
                )}

                {/* Loading overlay */}
                {loading && (
                    <div style={{
                        position: 'absolute', inset: 0, zIndex: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(240,246,255,0.85)', borderRadius: 10,
                    }}>
                        <div style={{ color: BRAND, fontWeight: 600, fontSize: '0.88rem' }}>Loading trail…</div>
                    </div>
                )}

                <Map
                    center={mapCenter}
                    zoom={mapZoom}
                    provider={provider}
                    onBoundsChanged={({ center, zoom }) => { setMapCenter(center); setMapZoom(zoom); }}
                    onClick={() => setActiveFixIdx(null)}
                    style={{ height: '100%', width: '100%' }}
                >
                    {/* SVG trail — no anchor so pigeon-maps still injects latLngToPixel */}
                    <TrailPolyline points={trailPoints} />

                    {/* Fix markers */}
                    {trailPoints.map((pt, i) => {
                        const isFirst = i === 0;
                        const isLast = i === trailPoints.length - 1;
                        const isActive = activeFixIdx === i;
                        const isBle = pt.type === 'ble';
                        const color = pt.type === 'ble' ? '#3b82f6' : pt.type === 'wifi' ? '#8b5cf6' : '#22c55e';
                        const size = (isFirst || isLast) ? 14 : 9;

                        return (
                            <Overlay key={i} anchor={[pt.lat, pt.lng]} offset={[size / 2, size / 2]}>
                                <div style={{ position: 'relative' }}>
                                    {/* Marker */}
                                    <div
                                        onClick={e => { e.stopPropagation(); setActiveFixIdx(prev => prev === i ? null : i); }}
                                        style={{
                                            width: size, height: size,
                                            background: color,
                                            border: `2px solid ${isActive ? '#1e3a5f' : '#fff'}`,
                                            borderRadius: isBle ? 2 : '50%',
                                            transform: isBle ? 'rotate(45deg)' : 'none',
                                            cursor: 'pointer',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                        }}
                                    />
                                    {/* S / E label */}
                                    {(isFirst || isLast) && (
                                        <span style={{
                                            position: 'absolute',
                                            top: -18,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            fontSize: '0.6rem',
                                            fontWeight: 800,
                                            background: isFirst ? '#1e3a5f' : BRAND,
                                            color: '#fff',
                                            padding: '1px 5px',
                                            borderRadius: 10,
                                            whiteSpace: 'nowrap',
                                            pointerEvents: 'none',
                                        }}>
                                            {isFirst ? 'Start' : 'End'}
                                        </span>
                                    )}
                                    {/* Tooltip */}
                                    {isActive && (
                                        <div style={{
                                            position: 'absolute',
                                            bottom: size + 8,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: 200,
                                            background: '#fff',
                                            border: '1px solid #dbeafe',
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 16px rgba(48,127,226,0.18)',
                                            zIndex: 100,
                                            pointerEvents: 'none',
                                        }}>
                                            <div style={{ background: BRAND, color: '#fff', padding: '5px 10px', fontSize: '0.75rem', fontWeight: 700 }}>
                                                {pt.type === 'ble' ? 'BLE Fix' : pt.type === 'wifi' ? 'WiFi Fix' : 'GPS Fix'} #{i + 1}
                                            </div>
                                            <div style={{ padding: '6px 10px', fontSize: '0.73rem', color: '#374151', lineHeight: 1.6 }}>
                                                <div>{tsToDate(pt.at).toLocaleString()}</div>
                                                {pt.location && <div style={{ color: '#64748b' }}>{pt.location}</div>}
                                                {pt.type === 'wifi' && (pt.floorLabel || pt.locationId || pt.freeTextLabel) && (
                                                    <div style={{ color: '#5b21b6', marginTop: 2 }}>
                                                        {pt.floorLabel && <div>{pt.floorLabel}</div>}
                                                        {(pt.locationId || pt.freeTextLabel) && (
                                                            <div>{pt.locationId ? (locationById[pt.locationId] ?? pt.locationId) : pt.freeTextLabel}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Overlay>
                        );
                    })}

                    <ZoomControl />
                </Map>

                {/* Controls overlay */}
                <div className="map-overlay-controls">
                    <button className="map-overlay-btn" onClick={resetMap} title="Fit trail">
                        <FaRegCompass size={16} />
                    </button>
                    <button
                        className="map-overlay-btn"
                        onClick={() => setSatelliteView(v => !v)}
                        title={satelliteView ? 'Street view' : 'Satellite'}
                    >
                        {satelliteView ? <MdMap size={16} /> : <MdSatelliteAlt size={16} />}
                    </button>
                </div>

                {/* Legend overlay */}
                <div className="map-overlay-legend">
                    <div className="map-overlay-legend-item">
                        <div style={{ width: 26, height: 3, borderRadius: 2, background: BRAND, flexShrink: 0 }} />
                        <span>Trail</span>
                    </div>
                    <div className="map-overlay-legend-item">
                        <div style={{
                            width: 9, height: 9, background: '#3b82f6', borderRadius: 2,
                            transform: 'rotate(45deg)', flexShrink: 0,
                        }} />
                        <span>BLE fix</span>
                    </div>
                    <div className="map-overlay-legend-item">
                        <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                        <span>GPS fix</span>
                    </div>
                    <div className="map-overlay-legend-item">
                        <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#8b5cf6', flexShrink: 0 }} />
                        <span>WiFi fix</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
